import { useEffect, useState } from "react";
import { Loader2, Save, ScrollText, Send } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

const FIELDS = [
  { key: "primary_message", label: "Primary host message", type: "text" },
  { key: "expanded_message", label: "Expanded explanation", type: "area" },
  { key: "vehicle_network_size", label: "Vehicle network size (number)", type: "number" },
  { key: "application_review_timeline", label: "Application review timeline", type: "text" },
  { key: "managed_hosting_note", label: "Managed hosting note", type: "area" },
  { key: "insurance_language", label: "Insurance language (legal-reviewed)", type: "area" },
];

export function HostProgramPanel() {
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [sending, setSending] = useState(false);
  const [testResult, setTestResult] = useState(null);
  useEffect(() => { api.get("/host-program/config").then((r) => setCfg(r.data)).catch(() => setCfg(null)); }, []);
  if (!cfg) return <p className="text-slate-400">Loading…</p>;
  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const sendTest = async () => {
    setSending(true); setTestResult(null);
    try {
      const r = await api.post("/admin/test-email", { to: testTo || undefined });
      setTestResult(r.data);
      if (r.data.ok) toast.success("Test email sent."); else toast.error("Test email not sent — see details.");
    } catch (e) { setTestResult({ ok: false, detail: formatApiError(e.response?.data?.detail) || "Request failed" }); }
    finally { setSending(false); }
  };

  const save = async () => {
    setBusy(true);
    try {
      const patch = Object.fromEntries(FIELDS.map((f) => [f.key, f.key === "vehicle_network_size" ? Number(cfg[f.key]) || 0 : cfg[f.key]]));
      const r = await api.put("/admin/host-program/config", patch);
      setCfg(r.data);
      toast.success("Host program updated. Changes are live on all host pages.");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div className="max-w-2xl space-y-5" data-testid="host-program-panel">
      <p className="text-sm text-slate-500">Single source of truth for host-facing policy wording. Editing here updates the Become-a-Host page and related content immediately.</p>
      {FIELDS.map((f) => (
        <div key={f.key}>
          <span className="label-caps text-slate-400 block mb-2">{f.label}</span>
          {f.type === "area" ? (
            <textarea value={cfg[f.key] || ""} onChange={(e) => set(f.key, e.target.value)} data-testid={`hp-${f.key}`} rows={3} className="belo-input w-full" />
          ) : (
            <input type={f.type} value={cfg[f.key] ?? ""} onChange={(e) => set(f.key, e.target.value)} data-testid={`hp-${f.key}`} className="belo-input w-full" />
          )}
        </div>
      ))}
      <button onClick={save} disabled={busy} data-testid="hp-save" className="inline-flex items-center gap-2 bg-blue-600 text-white px-6 py-2.5 rounded-full font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50">
        {busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />} Save changes
      </button>
      <p className="text-xs text-slate-400">Insurance, claims, and legal wording must be reviewed by the business's Massachusetts attorney and licensed insurance broker before publication.</p>

      <div className="border-t border-slate-100 pt-6 mt-2" data-testid="test-email-tool">
        <span className="label-caps text-slate-400 block mb-2">Email deliverability</span>
        <p className="text-sm text-slate-500 mb-3">Send a test email to confirm your belorentals.com sending domain is verified so host confirmations & payment reminders don't land in spam.</p>
        <div className="flex flex-wrap items-center gap-3">
          <input value={testTo} onChange={(e) => setTestTo(e.target.value)} data-testid="test-email-to" placeholder="you@example.com" className="belo-input flex-1 min-w-[220px]" />
          <button onClick={sendTest} disabled={sending} data-testid="test-email-send" className="inline-flex items-center gap-2 bg-slate-900 text-white px-5 py-2.5 rounded-full font-semibold hover:bg-slate-800 transition-colors disabled:opacity-50">
            {sending ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />} Send test
          </button>
        </div>
        {testResult && (
          <p data-testid="test-email-result" className={`text-sm mt-3 ${testResult.ok ? "text-emerald-600" : "text-rose-600"}`}>
            {testResult.ok ? `Sent from ${testResult.sender} to ${testResult.to}.` : `Not sent: ${testResult.detail || "unknown error"}`}
          </p>
        )}
      </div>
    </div>
  );
}

const ACTION_LABEL = {
  impersonate: "Signed in as", update_user: "Edited account", set_password: "Reset password",
  delete_user: "Deleted account", update_host_program_config: "Edited host program",
};

export function AuditLogPanel() {
  const [rows, setRows] = useState(null);
  useEffect(() => { api.get("/admin/audit-log").then((r) => setRows(r.data)).catch(() => setRows([])); }, []);
  if (!rows) return <p className="text-slate-400">Loading…</p>;

  return (
    <div data-testid="audit-log-panel">
      <p className="text-sm text-slate-500 mb-4 flex items-center gap-2"><ScrollText size={15} /> Privileged actions by admins & operations managers (most recent first).</p>
      {rows.length === 0 ? (
        <p className="text-slate-400" data-testid="audit-empty">No activity recorded yet.</p>
      ) : (
        <div className="overflow-x-auto rounded-2xl border border-slate-200">
          <table className="w-full text-sm min-w-[640px]">
            <thead className="bg-slate-50 text-left">
              <tr><th className="p-3 font-semibold text-slate-500">When</th><th className="p-3 font-semibold text-slate-500">Actor</th><th className="p-3 font-semibold text-slate-500">Action</th><th className="p-3 font-semibold text-slate-500">Target</th></tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} data-testid={`audit-row-${r.id}`} className="border-t border-slate-100">
                  <td className="p-3 text-slate-500 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                  <td className="p-3">
                    <span className="text-slate-900">{r.actor_email}</span>
                    <span className={`ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded-full ${r.actor_role === "operations_manager" ? "bg-violet-100 text-violet-700" : "bg-blue-100 text-blue-700"}`}>{r.actor_role === "operations_manager" ? "ops mgr" : r.actor_role}</span>
                  </td>
                  <td className="p-3 text-slate-700">{ACTION_LABEL[r.action] || r.action}{r.meta?.role ? ` → ${r.meta.role}` : ""}</td>
                  <td className="p-3 text-slate-500">{r.target_label || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
